import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { listSystemUserLoginLogs } from "../../services/systemUserService";
import {
  TOKENS,
  Icon,
  ICONS,
  Avatar,
  EmptyState,
  Spinner,
  inputClass,
  Pagination,
  usePagination,
  DEFAULT_PAGE_SIZE,
} from "../../components/common/DashboardUI";

function errMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

export default function SystemUserLoginLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    listSystemUserLoginLogs(params)
      .then(setLogs)
      .catch((err) => toast.error(errMessage(err, "Could not load login logs.")))
      .finally(() => setLoading(false));
  }, [search, fromDate, toDate]);

  // Debounce the search/filter-driven refetch.
  useEffect(() => {
    const t = setTimeout(fetchLogs, 300);
    return () => clearTimeout(t);
  }, [fetchLogs]);

  // listSystemUserLoginLogs() returns a plain array (no server-side
  // pagination) — paginate client-side over the already search/date-filtered
  // `logs`.
  const { page, setPage, totalPages, pageItems } = usePagination(logs, {
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey: `${search}|${fromDate}|${toDate}`,
  });

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
            Login Logs
          </h1>
          <p className="text-sm text-gray-500">Sign-in history for back-office operator accounts.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
          </span>
          <input
            className={`${inputClass} pl-9`}
            placeholder="Search by name, email, or employee no…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <input
          type="date"
          className={`${inputClass} w-auto`}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <input
          type="date"
          className={`${inputClass} w-auto`}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        {(fromDate || toDate) && (
          <button
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
            className="text-xs font-semibold text-gray-500 hover:text-gray-800"
          >
            Clear dates
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading login logs…</p>
          </div>
        ) : logs.length === 0 ? (
          <EmptyState icon={ICONS.clock} text="No login activity found." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left min-w-[760px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">User</th>
                  <th className="py-2 px-3 font-medium">Employee No.</th>
                  <th className="py-2 px-3 font-medium">Logged In At</th>
                  <th className="py-2 px-3 font-medium">IP Address</th>
                  <th className="py-2 pl-3 font-medium">Device / Browser</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((log) => (
                  <tr key={log.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={log.user_name} size={38} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                            {log.user_name}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{log.user_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">
                      {log.employee_no ?? "—"}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">
                      {log.logged_in_at ? new Date(log.logged_in_at).toLocaleString() : "—"}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap font-mono">
                      {log.ip_address || "—"}
                    </td>
                    <td
                      className="py-3 pl-3 text-xs text-gray-400 truncate max-w-xs"
                      title={log.user_agent || ""}
                    >
                      {log.user_agent || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && logs.length > 0 && (
          <div className="pt-4 mt-1 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
